// path: src/pages/IPA/Negociacoes/pages/NegociacaoFornecedor/verbaDeSellIn.ts

/**
 * O campo Verba da linha, com textos literais do protótipo menos os três marcados. **O estado vem
 * da CAUSA que o motor devolve, não dos números**, e nenhum número da linha é calculado aqui.
 */

import Decimal from 'decimal.js';

/** O selo da tela, na precedência que o motor já resolve: editado > do arquivo > sugerido. */
export type OrigemDaVerba = 'SUGGESTED' | 'EDITED' | 'FROM_FILE';

export type BaseDaMargemAlvo = 'EDITED_OBJECTIVE' | 'REGISTERED_OBJECTIVE' | 'RETAIL_PRICE';

/** Por que não há sugestão. Cada um tem texto próprio: reagrupá-los erra a causa com convicção. */
export type MotivoSemSugestao =
  | 'NO_NEW_COST'
  | 'NO_COMPETITOR_PRICE'
  | 'NO_TARGET_MARGIN'
  | 'NO_FROZEN_COST_MODE'
  | 'MARGIN_ALREADY_PRESERVED';

/** Todos opcionais: campo ausente na resposta não é dado ausente. */
export type LinhaComVerba = {
  sellinAllowance?: number | null;
  sellinAllowanceOrigin?: OrigemDaVerba | null;
  suggestedSellinAllowance?: number | null;
  sellinAllowanceMarginBasis?: BaseDaMargemAlvo | null;
  noSellinAllowanceSuggestionReason?: MotivoSemSugestao | null;
  sellinAllowanceToSupplier?: number | null;
  competitorsPrice?: number | null;
  purchaseTaxPercentage?: number | null;
};

export type EstadoDaVerba =
  | 'SEM_CONTRATO'
  | 'SEM_NOVO_CUSTO'
  | 'SEM_PRECO_DE_CONCORRENCIA'
  | 'SEM_MARGEM_ALVO'
  | 'SEM_MODO_DE_CUSTO_CONGELADO'
  | 'MARGEM_PRESERVADA'
  | 'SEM_VERBA_INFORMADA'
  | 'VERBA_PRESENTE'
  | 'ERRO';

/** Os três selos, em minúsculas — a caixa alta na tela é `text-transform` da folha de estilo. */
export type SeloDaVerba = 'sugerido' | 'do arquivo' | 'editado';

export type MolduraDaVerba = 'NORMAL' | 'SUGERIDO' | 'ERRO' | 'TRAVADO';

export type MotivoDeRecusa = 'NEGATIVA' | 'ALCANCA_O_NOVO_CUSTO';

/** A unidade em que o campo está sendo digitado e lido. O valor canônico é sempre em reais. */
export type UnidadeDaVerba = 'REAIS' | 'PONTOS';

/** Decide a cor na folha de estilo. */
export type TomDoSubtexto = 'neutro' | 'ok' | 'falta' | 'destaque';

export type SegmentoDeTexto = { texto: string; tom: TomDoSubtexto };

/** `origem` nula quer dizer "ele não mexeu": é dessa distinção que o desfazer tira o destino. */
export type EdicaoDaVerba = {
  /** O ultimo ACEITO, e o unico que vai ao servidor. */
  valor: number | null;
  /** O recusado, separado do aceito porque a tela tem de exibi-lo em vez de engolir a tecla. */
  valorRecusado: number | null;
  origem: OrigemDaVerba | null;
  recusa: MotivoDeRecusa | null;
  /** Decide se os números que o motor devolveu são DESTE valor ou do anterior. */
  sincronizada: boolean;
};

export const SEM_EDICAO: EdicaoDaVerba = { valor: null, valorRecusado: null, origem: null, recusa: null, sincronizada: false };

export const PLACEHOLDER_DA_VERBA = '--';

export const TITULO_DO_BOTAO_DE_UNIDADE = 'Alternar entre R$ e pontos de margem';

/** Literais do protótipo, exceto os três marcados. */
export const TEXTO_DA_VERBA = {
  SEM_NOVO_CUSTO: 'informe o novo custo',
  SEM_PRECO_DE_CONCORRENCIA: 'sem sugestão · sem preço de concorrência',
  /** PROVISÓRIO — pendente de redação do produto. O protótipo não tem frase para esta causa. */
  SEM_MARGEM_ALVO: 'sem sugestão · sem margem-alvo',
  /** PROVISÓRIO — pendente de redação do produto. Negociação fechada sem régua de conversão. */
  SEM_MODO_DE_CUSTO_CONGELADO: 'sem sugestão · sem modo de custo congelado',
  MARGEM_PRESERVADA: 'margem preservada sem verba',
  SEM_VERBA_INFORMADA_PREFIXO: 'sem verba informada · ideal R$ ',
  RECOMPOE_PREFIXO: 'recompõe ',
  RECOMPOE_SUFIXO: ' p.p. de margem',
  POR_UNIDADE_SUFIXO: ' por unidade',
  AO_FORNECEDOR_PREFIXO: 'ao fornecedor R$ ',
  SEGUNDA_LINHA_PREFIXO: '→ ao fornecedor R$ ',
  SEGUNDA_LINHA_SUFIXO: ' no custo de entrada',
  FALTA_PREFIXO: ' · falta ',
  ACIMA_DO_NECESSARIO: ' · acima do necessário',
  ERRO_MAIOR_QUE_O_CUSTO: 'verba maior que o custo',
  /** PROVISÓRIO — pendente de redação do produto. */
  ERRO_VERBA_NEGATIVA: 'verba não pode ser negativa',
} as const;

/** Uma frase por motivo: com uma só, quem digita negativo vai consertar a coisa errada. */
const FRASE_DA_RECUSA: Record<MotivoDeRecusa, string> = {
  NEGATIVA: TEXTO_DA_VERBA.ERRO_VERBA_NEGATIVA,
  ALCANCA_O_NOVO_CUSTO: TEXTO_DA_VERBA.ERRO_MAIOR_QUE_O_CUSTO,
};

/** Três sufixos porque o desfazer tem três destinos. Ver {@link destinoDoDesfazer}. */
export const MENU_DA_VERBA = {
  DESFAZER: 'Desfazer edição de verba',
  DESFAZER_VOLTA_PARA_SUGERIDO: ' (volta para sugerido)',
  DESFAZER_VOLTA_PARA_DO_ARQUIVO: ' (volta para do arquivo)',
  DESFAZER_LIMPA_A_VERBA: ' (limpa a verba)',
  APLICAR_IDEAL: 'Aplicar a verba ideal',
  LIMPAR: 'Limpar verba',
  CABECALHO: 'Verba',
} as const;

/** **A origem EDITADA num campo vazio e o que faz a limpeza durar** contra o recalculo do motor. */
export const LIMPAR_A_VERBA: DestinoDoDesfazer = {
  verba: null,
  origem: 'EDITED',
  sufixo: '',
};

export type CampoDaVerba = {
  estado: EstadoDaVerba;
  moldura: MolduraDaVerba;
  selo: SeloDaVerba | null;
  /** Em reais no custo líquido. Nulo é campo vazio; zero também. */
  valor: number | null;
  editavel: boolean;
  desabilitado: boolean;
  somenteLeitura: boolean;
  /** Primeira linha de subtexto; o selo é desenhado à parte. */
  linha1: SegmentoDeTexto[];
  /** A verba ao fornecedor. Vazia quando o motor não tem o número deste valor. */
  linha2: SegmentoDeTexto[];
  faltaParaAIdeal: number | null;
  verbaSugerida: number | null;
  pontosRecompostos: number | null;
  verbaAoFornecedor: number | null;
  /** A origem que a linha tinha ANTES de o comprador editar. */
  origemBase: OrigemDaVerba | null;
  /** Viaja no campo para o rotulo e a acao do menu sairem da MESMA resolucao. */
  destinoDoDesfazer: DestinoDoDesfazer;
};

export type OpcoesDoCampoDaVerba = {
  /** O novo custo da linha já no líquido. Nulo é o que desabilita o campo. */
  novoCustoLiquido?: number | null;
  negociacaoFechada?: boolean;
  /** A linha 1 mostra sempre a leitura que o campo NÃO exibe. */
  unidade?: UnidadeDaVerba;
};

const ESCALA_MONETARIA = 2;

/** Pontos de margem saem com UMA decimal; reais, com duas. */
const ESCALA_DOS_PONTOS = 1;

/** Diferença abaixo de meio centavo não é gap. */
const PISO_DO_GAP = 0.005;

const ehNumero = (valor: number | null | undefined): valor is number =>
  valor !== null && valor !== undefined && Number.isFinite(valor);

/** Nulo e zero são a mesma coisa para este campo: campo vazio. */
const valorOuVazio = (valor: number | null | undefined): number | null =>
  ehNumero(valor) && valor !== 0 ? valor : null;

const emReais = (valor: number) => new Decimal(valor).toFixed(ESCALA_MONETARIA).replace('.', ',');

const emPontos = (valor: number) => new Decimal(valor).toFixed(ESCALA_DOS_PONTOS).replace('.', ',');

/** `verba / preço de concorrência`, porque a margem do módulo divide pelo PREÇO. */
export const pontosDeMargemDaVerba = (verba: number | null | undefined, precoDeConcorrencia?: number | null): number | null => {
  if (!ehNumero(verba) || !ehNumero(precoDeConcorrencia) || precoDeConcorrencia <= 0) return null;

  return new Decimal(verba).dividedBy(precoDeConcorrencia).times(100).toDecimalPlaces(ESCALA_MONETARIA).toNumber();
};

export const verbaDosPontosDeMargem = (pontos: number | null | undefined, precoDeConcorrencia?: number | null): number | null => {
  if (!ehNumero(pontos) || !ehNumero(precoDeConcorrencia) || precoDeConcorrencia <= 0) return null;

  return new Decimal(pontos).dividedBy(100).times(precoDeConcorrencia).toDecimalPlaces(ESCALA_MONETARIA).toNumber();
};

/** A régua do motor, contra o custo LÍQUIDO. O negativo só chega com o sinal ligado no campo. */
export const recusaDaVerba = (verba: number | null, novoCustoLiquido?: number | null): MotivoDeRecusa | null => {
  if (!ehNumero(verba)) return null;
  if (verba < 0) return 'NEGATIVA';
  if (!ehNumero(novoCustoLiquido) || verba >= novoCustoLiquido) return 'ALCANCA_O_NOVO_CUSTO';
  return null;
};

/** Valor recusado NÃO entra: a edição anterior permanece, com o motivo da recusa ao lado. */
export const digitarAVerba = (edicao: EdicaoDaVerba, valor: number | null, novoCustoLiquido?: number | null): EdicaoDaVerba => {
  const valorNormalizado = valorOuVazio(valor);

  const recusa = recusaDaVerba(valorNormalizado, novoCustoLiquido);

  // O recusado entra para ser EXIBIDO; o ultimo aceito continua guardado para o servidor.
  if (recusa) return { ...edicao, valorRecusado: valorNormalizado, recusa };

  return { valor: valorNormalizado, valorRecusado: null, origem: 'EDITED', recusa: null, sincronizada: false };
};

/** Pareado com `EstadoPersistidoDaVerbaNaGravacao`. Ausente é "o motor não disse". */
export type VerbaGravadaPeloMotor = {
  sellinAllowance?: number | null;
  sellinAllowanceOrigin?: OrigemDaVerba | null;
};

/**
 * **Adota o valor que voltou, e não só marca como sincronizada:** em verba sugerida o motor a
 * RECALCULA. **Ele pode gravar VAZIO, e vazio se aplica.**
 */
export const marcarVerbaComoGravada = (edicao: EdicaoDaVerba, gravado: VerbaGravadaPeloMotor): EdicaoDaVerba => {
  // Recusa pendente: o recusado nunca foi ao servidor, então o que voltou é o acordo de ANTES.
  if (edicao.recusa !== null) return { ...edicao, sincronizada: true };

  // Origem ausente: "o motor não disse". Não acontece por contrato, e preservar é o seguro.
  const origem = gravado.sellinAllowanceOrigin;
  if (origem === undefined) return { ...edicao, sincronizada: true };

  // Origem vazia: o motor APAGOU o acordo, e cair fora da edição devolve a palavra à linha.
  if (origem === null) return SEM_EDICAO;

  return { valor: gravado.sellinAllowance ?? null, valorRecusado: null, origem, recusa: null, sincronizada: true };
};

export const desfazerAVerba = (_edicao: EdicaoDaVerba): EdicaoDaVerba => SEM_EDICAO;

/** Diferente do desfazer: o alvo é a SUGESTÃO, mesmo na linha cujo valor vigente é o do arquivo. */
export const aplicarVerbaIdeal = (_edicao: EdicaoDaVerba, verbaSugerida: number | null | undefined): EdicaoDaVerba => ({
  valor: valorOuVazio(verbaSugerida),
  valorRecusado: null,
  origem: 'SUGGESTED',
  recusa: null,
  sincronizada: false,
});

/** A edição é DESCARTADA: guardá-la faria o campo ressuscitar um acordo que o comprador desfez. */
export const apagarONovoCusto = (_edicao: EdicaoDaVerba): EdicaoDaVerba => SEM_EDICAO;

/** Decide se a tela pode adiantar os derivados. Vale o ultimo ACEITO; o recusado nao entra. */
export const linhaTemVerbaEmJogo = (linha: LinhaComVerba, edicao: EdicaoDaVerba = SEM_EDICAO): boolean => {
  const houveEdicao = edicao.origem !== null;
  const valor = houveEdicao ? edicao.valor : linha.sellinAllowance;

  return valorOuVazio(valor) !== null;
};

const seloDaOrigem = (origem: OrigemDaVerba | null | undefined): SeloDaVerba | null => {
  switch (origem) {
    case 'EDITED':
      return 'editado';
    case 'FROM_FILE':
      return 'do arquivo';
    case 'SUGGESTED':
      return 'sugerido';
    default:
      return null;
  }
};

/** **Os três saem juntos daqui**, porque divergem se resolvidos em separado. Nulas = LIMPEZA. */
export type DestinoDoDesfazer = {
  verba: number | null;
  origem: OrigemDaVerba | null;
  sufixo: string;
};

const LIMPA_A_VERBA: DestinoDoDesfazer = { verba: null, origem: null, sufixo: MENU_DA_VERBA.DESFAZER_LIMPA_A_VERBA };

/** **Decide os ramos o que é RECUPERÁVEL:** a edição sobrescreve a coluna do valor do arquivo. */
export const destinoDoDesfazer = (linha: LinhaComVerba, edicao: EdicaoDaVerba = SEM_EDICAO): DestinoDoDesfazer => {
  const origemGravada = linha.sellinAllowanceOrigin ?? null;
  const origemVigente = edicao.origem ?? origemGravada;
  const verbaSugerida = valorOuVazio(linha.suggestedSellinAllowance);

  if (edicao.origem !== null && origemGravada === 'FROM_FILE') {
    return {
      verba: valorOuVazio(linha.sellinAllowance),
      origem: 'FROM_FILE',
      sufixo: MENU_DA_VERBA.DESFAZER_VOLTA_PARA_DO_ARQUIVO,
    };
  }

  if (origemVigente === 'EDITED' && verbaSugerida !== null) {
    return { verba: verbaSugerida, origem: 'SUGGESTED', sufixo: MENU_DA_VERBA.DESFAZER_VOLTA_PARA_SUGERIDO };
  }

  return LIMPA_A_VERBA;
};

const carregaOContratoDaVerba = (linha: LinhaComVerba) =>
  linha.sellinAllowance !== undefined ||
  linha.sellinAllowanceOrigin !== undefined ||
  linha.suggestedSellinAllowance !== undefined ||
  linha.noSellinAllowanceSuggestionReason !== undefined;

const CAMPO_VAZIO: CampoDaVerba = {
  estado: 'SEM_CONTRATO',
  moldura: 'NORMAL',
  selo: null,
  valor: null,
  editavel: false,
  desabilitado: true,
  somenteLeitura: false,
  linha1: [],
  linha2: [],
  faltaParaAIdeal: null,
  verbaSugerida: null,
  pontosRecompostos: null,
  verbaAoFornecedor: null,
  origemBase: null,
  destinoDoDesfazer: LIMPA_A_VERBA,
};

const TEXTO_DO_MOTIVO: Record<Exclude<MotivoSemSugestao, 'NO_NEW_COST' | 'MARGIN_ALREADY_PRESERVED'>, string> = {
  NO_COMPETITOR_PRICE: TEXTO_DA_VERBA.SEM_PRECO_DE_CONCORRENCIA,
  NO_TARGET_MARGIN: TEXTO_DA_VERBA.SEM_MARGEM_ALVO,
  NO_FROZEN_COST_MODE: TEXTO_DA_VERBA.SEM_MODO_DE_CUSTO_CONGELADO,
};

const ESTADO_DO_MOTIVO: Record<MotivoSemSugestao, EstadoDaVerba> = {
  NO_NEW_COST: 'SEM_NOVO_CUSTO',
  NO_COMPETITOR_PRICE: 'SEM_PRECO_DE_CONCORRENCIA',
  NO_TARGET_MARGIN: 'SEM_MARGEM_ALVO',
  NO_FROZEN_COST_MODE: 'SEM_MODO_DE_CUSTO_CONGELADO',
  MARGIN_ALREADY_PRESERVED: 'MARGEM_PRESERVADA',
};

/** A ordem dos ramos importa: sem novo custo desabilita, e vem antes de qualquer outra ausência. */
export const resolverCampoDaVerba = (
  linha: LinhaComVerba,
  edicao: EdicaoDaVerba = SEM_EDICAO,
  opcoes: OpcoesDoCampoDaVerba = {},
): CampoDaVerba => {
  if (!carregaOContratoDaVerba(linha)) return CAMPO_VAZIO;

  const { novoCustoLiquido, negociacaoFechada = false, unidade = 'REAIS' } = opcoes;

  const origemBase = linha.sellinAllowanceOrigin ?? null;
  const verbaSugerida = valorOuVazio(linha.suggestedSellinAllowance);

  /** Custo apagado devolve nulo, e dado faltando na conta devolve NaN. */
  const temNovoCusto = ehNumero(novoCustoLiquido);

  const motivoDaLeitura = linha.noSellinAllowanceSuggestionReason ?? null;

  /** Duas vencem o motivo da leitura: o novo custo NA LINHA vence `NO_NEW_COST`, e a sugestão
   * vence qualquer um. Os outros quatro ficam. */
  const motivoVencidoPelaSugestao = verbaSugerida !== null;

  const motivo = (temNovoCusto && motivoDaLeitura === 'NO_NEW_COST') || motivoVencidoPelaSugestao ? null : motivoDaLeitura;

  // O único motivo que DESABILITA, e a edição não sobrevive a ele.
  if (!temNovoCusto) {
    return {
      ...CAMPO_VAZIO,
      estado: 'SEM_NOVO_CUSTO',
      linha1: [{ texto: TEXTO_DA_VERBA.SEM_NOVO_CUSTO, tom: 'neutro' }],
      somenteLeitura: negociacaoFechada,
      moldura: negociacaoFechada ? 'TRAVADO' : 'NORMAL',
      origemBase,
    };
  }

  // A recusa substitui o subtexto INTEIRO e leva a moldura ao estado de erro.
  if (edicao.recusa) {
    return {
      ...CAMPO_VAZIO,
      estado: 'ERRO',
      moldura: 'ERRO',
      // SEM selo: selo mais frase pede duas linhas visuais onde a grade tem uma, e a frase corta.
      selo: null,
      // Exibe o que ele DIGITOU, e o valor nao entra em conta nenhuma deste ramo.
      valor: edicao.valorRecusado ?? valorOuVazio(edicao.origem !== null ? edicao.valor : linha.sellinAllowance),
      editavel: !negociacaoFechada,
      desabilitado: false,
      linha1: [{ texto: FRASE_DA_RECUSA[edicao.recusa], tom: 'falta' }],
      verbaSugerida,
      origemBase,
      destinoDoDesfazer: destinoDoDesfazer(linha, edicao),
    };
  }

  // A edição local vence a linha enquanto existir; sem ela, o autor é o motor.
  const houveEdicao = edicao.origem !== null;
  const valor = valorOuVazio(houveEdicao ? edicao.valor : linha.sellinAllowance);
  const origem = houveEdicao ? edicao.origem : origemBase;

  const pontosRecompostos = pontosDeMargemDaVerba(valor, linha.competitorsPrice);

  // O número do fornecedor é do MOTOR, e só vale enquanto o valor na tela é o que ele viu.
  const numeroDoMotorEDesteValor = !houveEdicao || edicao.sincronizada;
  const verbaAoFornecedor = numeroDoMotorEDesteValor ? valorOuVazio(linha.sellinAllowanceToSupplier) : null;

  const linha1: SegmentoDeTexto[] = [];
  const linha2: SegmentoDeTexto[] = [];

  let estado: EstadoDaVerba;

  if (valor !== null) {
    estado = 'VERBA_PRESENTE';

    if (pontosRecompostos !== null) {
      linha1.push(
        unidade === 'REAIS'
          ? { texto: `${TEXTO_DA_VERBA.RECOMPOE_PREFIXO}${emPontos(pontosRecompostos)}${TEXTO_DA_VERBA.RECOMPOE_SUFIXO}`, tom: 'neutro' }
          : { texto: `R$ ${emReais(valor)}${TEXTO_DA_VERBA.POR_UNIDADE_SUFIXO}`, tom: 'neutro' },
      );
    } else if (verbaAoFornecedor !== null) {
      // Sem margem-alvo não há pontos a afirmar, e a linha 1 vira a leitura do fornecedor.
      linha1.push({ texto: `${TEXTO_DA_VERBA.AO_FORNECEDOR_PREFIXO}${emReais(verbaAoFornecedor)}`, tom: 'neutro' });
    }
  } else if (motivo === 'MARGIN_ALREADY_PRESERVED') {
    estado = 'MARGEM_PRESERVADA';
    linha1.push({ texto: TEXTO_DA_VERBA.MARGEM_PRESERVADA, tom: 'ok' });
  } else if (motivo !== null) {
    estado = ESTADO_DO_MOTIVO[motivo];
    linha1.push({ texto: TEXTO_DO_MOTIVO[motivo as keyof typeof TEXTO_DO_MOTIVO], tom: 'neutro' });
  } else if (verbaSugerida !== null) {
    estado = 'SEM_VERBA_INFORMADA';
    linha1.push({ texto: `${TEXTO_DA_VERBA.SEM_VERBA_INFORMADA_PREFIXO}${emReais(verbaSugerida)}`, tom: 'neutro' });
  } else {
    estado = 'SEM_VERBA_INFORMADA';
  }

  // Só quando há verba E ideal com que comparar.
  let faltaParaAIdeal: number | null = null;

  if (valor !== null && verbaSugerida !== null) {
    const diferenca = new Decimal(verbaSugerida).minus(valor);

    if (diferenca.abs().greaterThanOrEqualTo(PISO_DO_GAP)) {
      if (diferenca.greaterThan(0)) {
        faltaParaAIdeal = diferenca.toDecimalPlaces(ESCALA_MONETARIA).toNumber();
        linha1.push({ texto: `${TEXTO_DA_VERBA.FALTA_PREFIXO}${emReais(faltaParaAIdeal)}`, tom: 'falta' });
      } else {
        linha1.push({ texto: TEXTO_DA_VERBA.ACIMA_DO_NECESSARIO, tom: 'ok' });
      }
    }
  }

  /**
   * **A margem já está preservada e a verba fica**, porque verba editada é acordo. Reaproveita o
   * texto do gap acima, e os dois nunca saem juntos: aqui a sugestão vem vazia.
   */
  if (valor !== null && motivo === 'MARGIN_ALREADY_PRESERVED') {
    linha1.push({ texto: TEXTO_DA_VERBA.ACIMA_DO_NECESSARIO, tom: 'ok' });
  }

  // Só com o número do motor E pontos: sem margem-alvo a linha 1 já traz a leitura do fornecedor.
  if (valor !== null && verbaAoFornecedor !== null && pontosRecompostos !== null) {
    linha2.push({ texto: `${TEXTO_DA_VERBA.SEGUNDA_LINHA_PREFIXO}${emReais(verbaAoFornecedor)}`, tom: 'destaque' });
    linha2.push({ texto: TEXTO_DA_VERBA.SEGUNDA_LINHA_SUFIXO, tom: 'neutro' });
  }

  const moldura: MolduraDaVerba = negociacaoFechada ? 'TRAVADO' : origem === 'SUGGESTED' ? 'SUGERIDO' : 'NORMAL';

  return {
    estado,
    moldura,
    selo: seloDaOrigem(origem),
    valor,
    editavel: !negociacaoFechada,
    desabilitado: false,
    somenteLeitura: negociacaoFechada,
    linha1,
    linha2,
    faltaParaAIdeal,
    verbaSugerida,
    pontosRecompostos,
    verbaAoFornecedor,
    origemBase,
    destinoDoDesfazer: destinoDoDesfazer(linha, edicao),
  };
};
